import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import {
  INITIATIVE_LIST_TABS,
  initiativesSearchSchema,
  useInitiativePreparerEligibility,
  useInitiatives,
} from '@/hooks/queries/credit/useInitiatives'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useLocale } from '@/hooks/useLocale'
import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import { formatCurrency } from '@/lib/utils/numbers'
import type { InitiativeListTab } from '@repo/constants'
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon, SearchIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateInitiativeModal } from './-components/CreateInitiativeModal'
import { InitiativeRowActions } from './-components/InitiativeRowActions'
import { InitiativeStatusBadge } from './-components/InitiativeStatusBadge'
import { localMidnight } from './-data/initiativeDates'
import { InitiativesEmptyState } from './-components/InitiativesEmptyState'

export const Route = createFileRoute('/_auth/_modules/credit/initiatives/')({
  component: RouteComponent,
  validateSearch: (search) => initiativesSearchSchema.parse(search),
})

const SEARCH_DEBOUNCE_MS = 300

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const locale = useLocale()
  const { formatDateLong, formatDateWithTime, formatDistanceToNowLocalized } = useLocalizedDate()

  const tab: InitiativeListTab = search.tab ?? 'current'
  const [searchDraft, setSearchDraft] = useState(search.search ?? '')
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Debounced into the URL, which is what the query reads.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.search ?? '') !== searchDraft) {
        navigate({ search: (prev) => ({ ...prev, search: searchDraft || undefined }) })
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchDraft, search.search, navigate])

  const initiativesQuery = useInitiatives(tab, search.search)
  const rows = initiativesQuery.data?.rows ?? []
  const hasAnyInitiatives = (initiativesQuery.data?.totalCount ?? 0) > 0

  const canPrepare = useInitiativePreparerEligibility().data?.canPrepare

  const onTabChange = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        tab: value === 'current' ? undefined : (value as InitiativeListTab),
      }),
    })
  }

  const openDetail = (id: number) => {
    navigate({ to: '/credit/initiatives/$id', params: { id: String(id) } })
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="sm">
          <PageHeader>{t('initiatives.pageHeader')}</PageHeader>
          <Text tone="muted" className="max-w-2xl text-pretty">
            {t('initiatives.pageDescription')}
          </Text>
        </VStack>
        <div className="flex shrink-0 items-center gap-2">
          <div className="relative">
            <SearchIcon className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
            <Input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder={t('initiatives.searchPlaceholder')}
              className="bg-background w-56 pl-8"
            />
          </div>
          {canPrepare === false ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                {/* Radix swallows pointer events on disabled buttons — the span carries the hover. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button type="button" disabled>
                      <PlusIcon />
                      {t('initiatives.newInitiative')}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t('initiatives.errors.initiativeNotEligiblePreparer')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button type="button" onClick={() => setIsCreateOpen(true)}>
              <PlusIcon />
              {t('initiatives.newInitiative')}
            </Button>
          )}
        </div>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <VStack gap="sm">
            <Spread>
              <Tabs value={tab} onValueChange={onTabChange}>
                <TabsList>
                  {INITIATIVE_LIST_TABS.map((value) => (
                    <TabsTrigger key={value} value={value}>
                      {t(`initiatives.tabs.${value}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <Text tone="muted">
                {t('datatable.returnedResults', { ns: 'common', count: rows.length })}
              </Text>
            </Spread>

            {rows.length === 0 && !initiativesQuery.isPending ? (
              <InitiativesEmptyState
                tab={tab}
                searchTerm={search.search}
                hasAnyInitiatives={hasAnyInitiatives}
                canPrepare={canPrepare === true}
                onCreate={() => setIsCreateOpen(true)}
                onClearSearch={() => {
                  setSearchDraft('')
                  navigate({ search: (prev) => ({ ...prev, search: undefined }) })
                }}
                onViewEnded={() => onTabChange('ended')}
              />
            ) : (
              <div className="bg-background-level-1 w-full rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('initiatives.table.initiative')}</TableHead>
                      <TableHead>{t('initiatives.table.period')}</TableHead>
                      <TableHead className="text-right">
                        {t('initiatives.table.payerErps')}
                      </TableHead>
                      <TableHead className="text-right">{t('initiatives.table.uplift')}</TableHead>
                      <TableHead>{t('initiatives.table.status')}</TableHead>
                      <TableHead className="text-right">{t('initiatives.table.updated')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((initiative) => (
                      <TableRow
                        key={initiative.id}
                        className="cursor-pointer"
                        onClick={() => openDetail(initiative.id)}
                      >
                        <TableCell>
                          <VStack gap="none">
                            <Text weight="medium">{initiative.name}</Text>
                            <Text size="xs" tone="muted">
                              {initiative.versionNumber
                                ? t('initiatives.versionLabel', {
                                    number: initiative.versionNumber,
                                  })
                                : NO_DATA_AVAILABLE_SYMBOL}
                            </Text>
                          </VStack>
                        </TableCell>
                        <TableCell>
                          {`${formatDateLong(localMidnight(initiative.startDate))} – ${formatDateLong(localMidnight(initiative.endDate))}`}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {(initiative.payerCount ?? 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(locale, initiative.upliftTotal ?? 0)}
                        </TableCell>
                        <TableCell>
                          <VStack gap="none">
                            <InitiativeStatusBadge
                              status={initiative.status}
                              pendingSlot={initiative.pendingSlot}
                              startDate={initiative.startDate}
                              endDate={initiative.endDate}
                            />
                            {initiative.slaDueAt ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    {/* px-3 lines the text up with the badge label (same inset). */}
                                    <span className="w-fit cursor-default px-3">
                                      <Text size="xs" tone="muted" as="span">
                                        {t('initiatives.status.slaDueIn', {
                                          distance: formatDistanceToNowLocalized(
                                            new Date(initiative.slaDueAt),
                                          ),
                                        })}
                                      </Text>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t('initiatives.status.slaDueBy', {
                                      date: formatDateWithTime(new Date(initiative.slaDueAt)),
                                    })}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                          </VStack>
                        </TableCell>
                        <TableCell className="text-right">
                          <Text tone="muted" as="span">
                            {initiative.updatedAt
                              ? formatDistanceToNowLocalized(new Date(initiative.updatedAt), {
                                  addSuffix: true,
                                })
                              : NO_DATA_AVAILABLE_SYMBOL}
                          </Text>
                        </TableCell>
                        <TableCell
                          className="text-right"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <InitiativeRowActions
                            initiative={initiative}
                            canEdit={canPrepare === true}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </VStack>
        </CardContent>
      </Card>

      <CreateInitiativeModal
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={openDetail}
      />
    </PageContainer>
  )
}
